import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  acceptGoogleInvite,
  googleBrowserLogin,
  logoutBrowserSession,
  refreshBrowserSession,
} from '@/auth/auth-session';
import type { SessionUser } from '@/types';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';
interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  loginWithGoogle: (idToken: string) => Promise<void>;
  acceptInvite: (idToken: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let active = true;
    refreshBrowserSession().then(async (session) => {
      if (!active) return;
      if (session && session.portal !== 'admin') {
        const isAccountEntry = window.location.pathname === '/login'
          || window.location.pathname === '/accept-invite';
        if (isAccountEntry) {
          await logoutBrowserSession();
          if (!active) return;
          setUser(null);
          setStatus('anonymous');
          return;
        }
        window.location.replace(session.portal === 'owner' ? (import.meta.env.VITE_OWNER_URL ?? (import.meta.env.DEV ? 'http://localhost:5175' : 'https://owner.futsaly.com')) : (import.meta.env.VITE_PUBLIC_URL ?? (import.meta.env.DEV ? 'http://localhost:5174' : 'https://futsaly.com')));
        return;
      }
      setUser(session?.user ?? null);
      setStatus(session ? 'authenticated' : 'anonymous');
    });
    const expired = () => { setUser(null); setStatus('anonymous'); };
    window.addEventListener('futsaly-auth-expired', expired);
    return () => { active = false; window.removeEventListener('futsaly-auth-expired', expired); };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    user,
    loginWithGoogle: async (idToken) => {
      const session = await googleBrowserLogin(idToken);
      if (session.portal !== 'admin') {
        window.location.assign(session.portal === 'owner' ? (import.meta.env.VITE_OWNER_URL ?? (import.meta.env.DEV ? 'http://localhost:5175' : 'https://owner.futsaly.com')) : (import.meta.env.VITE_PUBLIC_URL ?? (import.meta.env.DEV ? 'http://localhost:5174' : 'https://futsaly.com')));
        return;
      }
      setUser(session.user);
      setStatus('authenticated');
    },
    acceptInvite: async (idToken, token) => {
      const session = await acceptGoogleInvite(idToken, token);
      setUser(session.user);
      setStatus('authenticated');
    },
    logout: async () => {
      await logoutBrowserSession();
      setUser(null);
      setStatus('anonymous');
    },
  }), [status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}
